import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { Bubble } from 'tea-component';
import { getLocale, useIntl } from 'umi';
import OverallPerformance from '../OverallPerformance';
import dashboardRequest from '@/pages/dashboard/utils/dashboardRequest';
import { useOverLimitTrigger } from '@/pages/dashboard/components/OverLimitTip';
import { useLocal, useWeekDayArr } from '@/pages/dashboard/utils/customHook';
import { formatFilterValues, formatNum, formatPercentNum, getRatioText } from '@/pages/dashboard/utils/utils';
import SvgIcon from '@/pages/home/components/SvgIcon';
import { downloadFileWithFileFormat, useNotificationOfDownloadTable } from "@/pages/dashboard/components/DownloadFormatModal";
import { getNameMap } from '@/pages/dashboard/_config/filterListMap';
import { getDefaultDateValue } from '@/pages/dashboard/_config/defualtTime';
import { PUBG_GAME_CODE_LIST } from '@/pages/dashboard/_config/constant';
import getMetricColumn from '@/pages/dashboard/utils/getMetricColumn';
import { getMonetUnit } from '@/pages/dashboard/utils/moneyUnit';
import { getExplainVal } from '@/pages/dashboard/utils/getExplainVal';

import './index.scss';
import { LANGUAGE_EN } from '@home/_config/config';
import { getName } from '@home/utils/common';

interface Props {
  gameCode: string;
  filterLoading: boolean;
  formValue: any;
  explainInfo: any;
}

const TAB_NAME: any = {
  Day: 'daily',
  Week: 'weekly',
  Month: 'monthly'
};
const widthMid = 170;

const sortMap: any = {
  by_channel: 'channel',
  by_country: 'country',
  by_network: 'network',
  by_region: 'region',
  by_system: 'system',
};

const NoSpendOverallPerformance = (props: Props) => {
  const { explainInfo, formValue, filterLoading, gameCode } = props;
  const { filterValue, timeType, diff } = formValue;
  const { rangeTime, ...filters } = filterValue || {};

  const isPUBGM = PUBG_GAME_CODE_LIST.includes(gameCode);
  const defaultTimeRange = getDefaultDateValue(filterValue?.timeType, true, isPUBGM);

  const timeRangeArr = diff ? defaultTimeRange : rangeTime;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [tableData, setTableData] = useState<any>({});
  const { overLimitBarTriggerKey, handleResponse: handleOverLimitResponse } = useOverLimitTrigger();
  const { isEN, locale, getLocalText } = useLocal();
  const [accumulation, setAccumulation] = useState<any[]>(['date']);
  const [tablePaging, setTablePaging] = useState({ pageIndex: 1, pageSize: 10 });
  const [sortValue, setSortValue] = useState<any>([]);
  const [columns, setColumns] = useState<any[]>([]);
  const [selectType, setSelectType] = useState('value_only');
  const weekDayArr = useWeekDayArr();
  const { setNoticeStatus } = useNotificationOfDownloadTable(getLocalText('ua_Overall_Performance'));
  const metricColumn = getMetricColumn();
  const UA_SEPARATED_OPTIONS = [
    {
      groupKey: 'separated',
      value: 'date',
      text: getLocalText('common_by_Date'),
      header: getLocalText('common_by_Date'),
      disabled: true,
    },
    {
      groupKey: 'separated',
      value: 'by_region',
      text: getLocalText('common_by_Region'),
      header: getLocalText('common_Region'),
    },
    {
      groupKey: 'separated',
      value: 'by_country',
      text: getLocalText('common_by_Country'),
      header: getLocalText('common_Country'),

    },
    {
      groupKey: 'separated',
      value: 'by_system',
      text: getLocalText('common_by_System'),
      header: getLocalText('common_System'),

    },
    {
      groupKey: 'separated',
      value: 'by_network',
      text: getLocalText('common_by_netWork_Type'),
      header: getLocalText('common_netWork_Type'),

    },
    {
      groupKey: 'separated',
      value: 'by_channel',
      text: getLocalText('common_by_Channel'),
      header: getLocalText('common_Channel'),

    },
  ];

  const getGroupCol = (accumulation: any[]) => {
    const nameMap = getNameMap(gameCode);
    return accumulation?.map((key: any) => ({
      key,
      header: UA_SEPARATED_OPTIONS.find(v => v.value === key)?.header,
      width: 170,
      align: 'center',
      render: (record: any) => {
        const metricsKey = sortMap?.[key];
        const dataValue = record?.[`${key}`];
        let value = getName(locale === LANGUAGE_EN, dataValue, nameMap?.[metricsKey]?.[dataValue] || dataValue);
        if (key === 'by_system') value = value || 'Others';
        if (key === 'by_system' && ['Sum', 'Average'].includes(record.date)) value = '-';
        return (<span>{value}</span>);
      }
    })) ?? [];
  };

  const columnRender = (col: any) => {
    const { valueType } = col;
    let render: any = null;


    // const isSumOrAvg = col?.key = ''
    if (valueType === 'percent') {
      render = (cvm: any) => {
        const isSumOrAvg = cvm.date === getLocalText('common_Average') || cvm.date === getLocalText('common_Sum');
        const num = cvm[col.key];
        // let numText = formatPercentNum(num)
        const numText = formatPercentNum(num, 2, false, true);
        const dod = cvm[`${col.key}_dod`];
        const { text, status } = getRatioText(dod);
        const isUp = status === 'up';
        const isEmpty = numText === '-' && status === 'no';

        return (
          <>
            <div className='table-desc-text-bg' style={{ width: `${num * 100}%`, }} />
            {
              <div className='table-desc dbd-ua-performance-table-desc'>
                {
                  isEmpty ? '-' : (
                    <>
                      <div className='table-desc-text'>{numText}</div>
                      {selectType === 'dod' && (
                        <div className='dbd-ua-performance-table-desc-ratio'>
                          {
                            status === 'no' ? (
                              isSumOrAvg ? '' : '-'
                            ) : (
                              <>
                                {
                                  status === 'zero' ?
                                    <>
                                      <div className={'table-desc-grey'}>{text}-</div>
                                    </> :
                                    <>
                                      <div className={isUp ? 'table-desc-green' : 'table-desc-red'}>{text}</div>
                                      <SvgIcon type={isUp ? 'green-up-new' : 'red-down-new'} className='table-desc-icon' />
                                    </>
                                }
                              </>
                            )
                          }
                        </div>
                      )}
                    </>
                  )
                }
              </div>

            }
          </>
        );
      };
    } else {
      render = (cvm: any) => {
        const num = cvm[col.key];
        const numText = formatNum(num, false, valueType === 'int');
        const dod = cvm[`${col.key}_dod`];
        const { text, status } = getRatioText(dod);
        const isUp = status === 'up';
        const isEmpty = numText === '-' && status === 'no';
        const isSumOrAvg = cvm.date === getLocalText('common_Average') || cvm.date === getLocalText('common_Sum');
        // numText = (numText === '0.00' || numText === 0.00) ? '0' : numText;
        return (
          <div className='table-desc dbd-ua-performance-table-desc'>
            {
              isEmpty ? '-' : (
                <>
                  <div className='table-desc-text'>{numText}</div>
                  {
                    selectType === 'dod' && (
                      <div className='dbd-ua-performance-table-desc-ratio'>
                        {
                          status === 'no' ? (
                            isSumOrAvg ? '' : '-'
                          ) : (
                            <>
                              {
                                status === 'zero' ?
                                  <>
                                    <div className={'table-desc-grey'}>{text}-</div>
                                  </> :
                                  <>
                                    <div className={isUp ? 'table-desc-green' : 'table-desc-red'}>{text}</div>
                                    <SvgIcon type={isUp ? 'green-up-new' : 'red-down-new'} className='table-desc-icon' />
                                  </>
                              }
                            </>
                          )
                        }
                      </div>
                    )
                  }
                </>
              )
            }
          </div>
        );
      };
    }

    return render;
  };

  const update = (config?: any) => {
    const fetchAccumulation = config?.accumulation ?? accumulation;
    const fetchTablePaging = config?.tablePaging ?? tablePaging;
    const fetchSortValue = config?.sortValue ?? sortValue;
    setAccumulation(fetchAccumulation);
    setTablePaging(fetchTablePaging);
    setSortValue(fetchSortValue);
    const params = {
      period: TAB_NAME[timeType],
      game_code: gameCode,
      start_time: timeRangeArr[0],
      end_time: timeRangeArr[1],
      filters: formatFilterValues(filters),
      accumulation: {
        by_channel: fetchAccumulation.includes('by_channel'),
        by_country: fetchAccumulation.includes('by_country'),
        by_network: fetchAccumulation.includes('by_network'),
        by_region: fetchAccumulation.includes('by_region'),
        by_system: fetchAccumulation.includes('by_system')
      },
      page: fetchTablePaging.pageIndex,
      page_size: fetchTablePaging.pageSize,
      sorted_by: fetchSortValue?.[0] ? (sortMap?.[fetchSortValue?.[0]?.by] || fetchSortValue?.[0]?.by) : 'date',
      desc: fetchSortValue?.[0] ? fetchSortValue?.[0]?.order === 'desc' : true
    };
    setLoading(true);
    setError(false);
    dashboardRequest({
      // 只调用英文接口, 前端自己到筛选器去做名称映射
      url: `/api/v1/dashboard/ua/no_spend/detail_table?language=en`,
      method: 'post',
      data: params,
      ignoreRequestId: false
    })
      .then((data) => {
        setTableData(data);
        handleOverLimitResponse(data?.front_logical_flag);
        setError(false);
      })
      .catch((err) => {
        console.log(err);
        setError(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleDownload = () => {
    const downloadPromise = downPromise?.();
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    if (downloadPromise) { // 有返回Promise对象时由内部处理下载过程
      // 展示正在下载中通知
      setNoticeStatus("loading");
      // 发起请求下载
      (async () => {
        try {
          const res = await downloadPromise;
          const fileName = `${getLocalText('ua_Overall_Performance').replace(/\s/g, '_')} ${moment().format('YYYY_MM_DD HH_mm_ss')}`;
          await downloadFileWithFileFormat(res, fileName, {
            onDownloadSuccess() {
              setNoticeStatus("success");
            },
            onDownloadError() {
              setNoticeStatus("error");
            }
          });
        } catch (e) {
          setNoticeStatus("error");
        }
      })();
    }
  };

  function downPromise() {
    const params = {
      period: TAB_NAME[timeType],
      game_code: gameCode,
      start_time: timeRangeArr[0],
      end_time: timeRangeArr[1],
      filters: formatFilterValues(filters),
      accumulation: {
        by_channel: accumulation.includes('by_channel'),
        by_country: accumulation.includes('by_country'),
        by_network: accumulation.includes('by_network'),
        by_region: accumulation.includes('by_region'),
        by_system: accumulation.includes('by_system')
      },
      show_dod: selectType === 'dod',
      page: tablePaging.pageIndex,
      page_size: tablePaging.pageSize,
      sorted_by: sortValue?.[0] ? sortValue?.[0]?.by : 'date',
      desc: sortValue?.[0] ? sortValue?.[0]?.order === 'desc' : true
    };
    return dashboardRequest({
      url: `/api/v1/dashboard/ua/no_spend/detail_down?language=${isEN ? 'en' : 'zh'}`,
      method: 'post',
      data: params,
      responseType: 'arraybuffer',
      rawData: true,
      ignoreRequestId: false
    });
  }
  const { formatMessage } = useIntl();
  useEffect(() => {
    const dateCol = {
      key: 'date',
      header: getLocalText('ua_Date'),
      align: 'left',
      width: 160,
      fixed: 'left',
      render(cvm: any) {
        /** Sum提示需要用到useState，
        *所以拿出来单独处理，
        *不然render里拿不到最新的的state
        */
        return <DateCell cvm={cvm} formatMessage={formatMessage} />;
      }
    };

    // 日期列, 周六日的颜色高亮 & Sum行增加提示
    function DateCell({ cvm, formatMessage }: any) {
      const [isIconHover, setIsIconHover] = useState(false);
      const locale = getLocale();


      const weekValue = moment(cvm.date).weekday();
      const weekStr = weekDayArr[weekValue];
      const isSunOrSat = weekValue === 5 || weekValue === 6;
      const showSumTip = cvm.date === 'Sum' || cvm.date === '汇总值';

      return (
        <div style={{ display: 'flex', alignItems: 'center', lineHeight: 1 }}>
          <span className='table-text'>
            {moment(cvm.date).isValid() ? moment(cvm.date).format(timeType === 'Month' ? 'YYYY-MM' : 'YYYY-MM-DD') : cvm.date}
          </span>
          {timeType === 'Month' ? <span style={{ display: 'inline-block', width: '0.4em' }}></span> :
            <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>{weekStr}</span>
          }
          {showSumTip && (
            <div className='port-item'>
              <div onMouseOver={() => { setIsIconHover(true) }} onMouseOut={() => { setIsIconHover(false) }} >
                <Bubble
                  dark
                  placement="top-start"
                  arrowPointAtCenter
                  style={{ maxWidth: locale === 'en-US' ? '235px' : '253px' }}
                  content={formatMessage({ id: 'overview_sum_tip' })}
                >
                  <div>
                    <SvgIcon className='port-icon' type={isIconHover ? 'alert-circle-action' : 'alert-circle-m'} />
                  </div>
                </Bubble>
              </div>
            </div>
          )}
        </div>
      );


    }
    const groupCol = getGroupCol(accumulation?.filter(item => item !== 'date'));
    const metricCols: any = [
      { width: widthMid, key: 'install', header: getLocalText('ua_table_Install'), valueType: 'int', rainbowKey: 'Install' },
      { width: widthMid, key: 'register', header: getLocalText('ua_Register'), valueType: 'int', rainbowKey: 'Register' },
      { width: widthMid + 40, key: 'install_register_rate', header: getLocalText('ua_Cohort_Register'), valueType: 'percent', rainbowKey: 'Cohort Register%' },
      { width: widthMid, key: 'd2', header: getLocalText('ua_D2'), valueType: 'percent', rainbowKey: 'D2' },
      { width: widthMid, key: 'd3', header: getLocalText('ua_D3'), valueType: 'percent', rainbowKey: 'D3' },
      { width: widthMid, key: 'd7', header: getLocalText('ua_D7'), valueType: 'percent', rainbowKey: 'D7' },
      { width: widthMid, key: 'd14', header: getLocalText('ua_D14'), valueType: 'percent', rainbowKey: 'D14' },
      { width: widthMid, key: 'd30', header: getLocalText('ua_D30'), valueType: 'percent', rainbowKey: 'D30' },
      { width: widthMid, key: 'spend', header: getLocalText('ua_table_spend', { unit: getMonetUnit() }), rainbowKey: 'Spend' },
      { width: isEN ? widthMid : widthMid + 20, key: 'cpi', header: getLocalText('ua_table_cpi', { unit: getMonetUnit() }), rainbowKey: 'CPI' },
      { width: isEN ? widthMid : widthMid + 20, key: 'cpa', header: getLocalText('ua_table_cpa', { unit: getMonetUnit() }), rainbowKey: 'CPA' },
      { width: widthMid, key: 'roas', header: getLocalText('ua_table_ROAS'), valueType: 'percent', rainbowKey: 'ROAS' },
      { width: isEN ? widthMid : widthMid + 30, key: 'roas_d1', header: getLocalText('ua_table_roasd1'), valueType: 'percent', rainbowKey: 'ROAS-D1' },
      { width: isEN ? widthMid : widthMid + 30, key: 'roas_d3', header: getLocalText('ua_table_roasd3'), valueType: 'percent', rainbowKey: 'ROAS-D3' },
      { width: isEN ? widthMid : widthMid + 30, key: 'roas_d7', header: getLocalText('ua_table_roasd7'), valueType: 'percent', rainbowKey: 'ROAS-D7' },
      { width: widthMid, key: 'ltv1', header: getLocalText('ua_LTV1'), rainbowKey: 'LTV1' },
      { width: widthMid, key: 'ltv3', header: getLocalText('ua_LTV3'), rainbowKey: 'LTV3' },
      { width: widthMid, key: 'ltv7', header: getLocalText('ua_LTV7'), rainbowKey: 'LTV7' },
      { width: widthMid, key: 'ltv14', header: getLocalText('ua_LTV14'), rainbowKey: 'LTV14' },
      { width: widthMid, key: 'ltv30', header: getLocalText('ua_LTV30'), rainbowKey: 'LTV30' },
      { width: widthMid, key: 'ltv180', header: getLocalText('ua_LTV180'), rainbowKey: 'LTV180' },
    ];

    metricCols.splice(8, 7);
    const isShowUpDown = selectType !== "value_only";
    metricCols.forEach((col: any) => {
      const metric: any = metricColumn?.[col?.key];
      const width = isShowUpDown ? metric?.width : metric?.onlyValueWidth;
      col.width = width ? width : col?.width;
      col.align = 'right';
      col.render = columnRender(col);
      const explain = getExplainVal(explainInfo, col?.rainbowKey, locale);
      // 指标说明
      if (explain) {
        col.explain = explain;
      }
    });

    setColumns([dateCol, ...groupCol, ...metricCols]);
  }, [accumulation, locale, explainInfo, selectType, formValue]);

  useEffect(() => {
    if (filterLoading) return;
    setTablePaging({ pageIndex: 1, pageSize: 10 });
    update({ tablePaging: { pageIndex: 1, pageSize: 10 } });
  }, [
    // gameCode,
    // locale,
    filterLoading,
    formValue,
  ]);

  return (
    <OverallPerformance
      title={getLocalText('ua_Overall_Performance')}
      tableData={tableData}
      loading={loading}
      error={error}
      timeType={timeType}
      selectType={selectType}
      setSelectType={setSelectType}
      columns={columns}
      tablePaging={tablePaging}
      sortValue={sortValue}
      accumulation={accumulation}
      accumulationList={UA_SEPARATED_OPTIONS}
      onUpdate={update}
      onColumnChange={setColumns}
      handleDownload={handleDownload}
      frontLogicalFlag={tableData?.front_logical_flag}
      overLimitBarTriggerKey={overLimitBarTriggerKey}
      gameCode={gameCode}
    />
  );
};

export default NoSpendOverallPerformance;